import React, { useEffect } from "react";
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Flex,
  Icon,
  Text,
} from "@chakra-ui/react";
import {
  MdDashboard,
  MdLocationOn,
  MdLocalActivity,
  MdEvStation,
  MdPayment,
  MdBolt,
  MdArticle,
} from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";

import StationOverView from "../tab_panels/StationOverView";
import Location from "../tab_panels/Coordinates";
import Facilities from "../tab_panels/Facilities";
import ChargePointTabs from "./ChargePointTabs";
import OcppLogs from "../tab_panels/OcppLogs";
import BillingPlan from "../tab_panels/BillingPlan";
import ChargingSessions from "../tab_panels/ChargingSessions";
import { loggedInUserState } from "atoms/loggedInUserState";
import { accessLevels } from "utils/accessLevels";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyAdmins } from "utils/accessLevels";
import { chargePointCodeState } from "atoms/chargePointCodeState";
import { chargePointConnectorIdState } from "atoms/chargePointConnectorState";
import { onlyUAdmins } from "utils/accessLevels";
import Roaming from "../components/Roaming";
import { FaTowerBroadcast } from "react-icons/fa6";

const LocationTabs = (props) => {
  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const [chargePointCode, setChargePointCode] =
    useRecoilState(chargePointCodeState);
  const [chargePointConnectorId, setChargePointConnectorId] = useRecoilState(
    chargePointConnectorIdState
  );
  const location = useLocation();
  const navigate = useNavigate();
  const isNewChargingStation = location.pathname === "/admin/locations/new";

  const loggedInUser = useRecoilValue(loggedInUserState);
  const role = loggedInUser?.role;
  const accessLevel = accessLevels[role]?.accessLevel;

  const clickHandler = (path, queryParams = "") => {
    navigate(`/admin/locations/view/${props?.id}/${path}${queryParams}`);
  };

  useEffect(() => {
    const path = location.pathname;
    switch (path) {
      case `/admin/locations/view/${props?.id}/overview`: {
        setActiveTabIndex(0);
        break;
      }
      // case `/admin/locations/view/${props?.id}/photos`: {
      //   setActiveTabIndex(1);
      //   break;
      // }
      case `/admin/locations/view/${props?.id}/coordinates`: {
        setActiveTabIndex(1);
        break;
      }
      case `/admin/locations/view/${props?.id}/facilities`: {
        setActiveTabIndex(2);
        break;
      }
      case `/admin/locations/view/${props?.id}/stations`: {
        setActiveTabIndex(3);
        setChargePointCode("");
        setChargePointConnectorId("");
        break;
      }
      case `/admin/locations/view/${props?.id}/ocpplogs`: {
        setActiveTabIndex(4);
        break;
      }
      case `/admin/locations/view/${props?.id}/billingplan`: {
        if (onlyAdmins.includes(role)) {
          setActiveTabIndex(5);
        } else {
          navigate("/404");
        }
        break;
      }
      case `/admin/locations/view/${props?.id}/sessions`: {
        setActiveTabIndex(6);
        break;
      }
      case `/admin/locations/view/${props?.id}/roaming`: {
        setActiveTabIndex(7);
        break;
      }
      case "/admin/locations/new": {
        setActiveTabIndex(0);
        break;
      }
      default:
        navigate("/404");
        break;
    }
  }, [location.pathname]);

  const getChargePointQueryParams = () => {
    return `?sid=${chargePointCode}&evseid=${chargePointConnectorId}`;
  };

  return (
    <Tabs variant="unstyled" isLazy index={activeTabIndex}>
      <TabList
        gap={{ base: 2, md: 4 }}
        px={{ base: 2, md: 1 }}
        py={2}
        bg="rgba(255, 255, 255, 0.6)"
        backdropFilter="blur(12px)"
        borderRadius="xl"
        border="1px solid rgba(245, 125, 54, 0.15)"
        position="relative"
        overflow="hidden"
        overflowX={{ base: "auto", md: "hidden" }}
        overflowY="hidden"
        flexWrap={{ base: "nowrap", md: "wrap" }}
        sx={{
          "&::-webkit-scrollbar": {
            height: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(245, 125, 54, 0.05)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(245, 125, 54, 0.3)",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "rgba(245, 125, 54, 0.5)",
          },
        }}
        _before={{
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "100%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <Tab
          _focus={{ boxShadow: "none" }}
          onClick={() => clickHandler("overview")}
          borderRadius="lg"
          px={{ base: 3, md: 4 }}
          py={2}
          fontWeight="500"
          color="gray.600"
          position="relative"
          zIndex="1"
          minW="fit-content"
          whiteSpace="nowrap"
          _selected={{
            bg: "rgba(245, 125, 54, 0.15)",
            color: "#f57d36",
            fontWeight: "600",
            boxShadow: "0 2px 4px rgba(245, 125, 54, 0.1)",
          }}
          _hover={{
            bg: "rgba(245, 125, 54, 0.08)",
            color: "#f57d36",
          }}
        >
          <Flex align="center" gap={{ base: 1, md: 2 }}>
            <Icon as={MdDashboard} boxSize={{ base: 3.5, md: 4 }} />
            <Text fontSize={{ base: "sm", md: "md" }}>Overview</Text>
          </Flex>
        </Tab>
        {!isNewChargingStation && (
          <>
            {/* <Tab
              _focus={{ boxShadow: "none" }}
              onClick={() => clickHandler("photos")}
            >
              Photos
            </Tab> */}
            <Tab
              _focus={{ boxShadow: "none" }}
              onClick={() => clickHandler("coordinates")}
              borderRadius="lg"
              px={{ base: 3, md: 4 }}
              py={2}
              fontWeight="500"
              color="gray.600"
              position="relative"
              zIndex="1"
              minW="fit-content"
              whiteSpace="nowrap"
              _selected={{
                bg: "rgba(245, 125, 54, 0.15)",
                color: "#f57d36",
                fontWeight: "600",
                boxShadow: "0 2px 4px rgba(245, 125, 54, 0.1)",
              }}
              _hover={{
                bg: "rgba(245, 125, 54, 0.08)",
                color: "#f57d36",
              }}
            >
              <Flex align="center" gap={{ base: 1, md: 2 }}>
                <Icon as={MdLocationOn} boxSize={{ base: 3.5, md: 4 }} />
                <Text fontSize={{ base: "sm", md: "md" }}>Coordinates</Text>
              </Flex>
            </Tab>
            <Tab
              _focus={{ boxShadow: "none" }}
              onClick={() => clickHandler("facilities")}
              borderRadius="lg"
              px={{ base: 3, md: 4 }}
              py={2}
              fontWeight="500"
              color="gray.600"
              position="relative"
              zIndex="1"
              minW="fit-content"
              whiteSpace="nowrap"
              _selected={{
                bg: "rgba(245, 125, 54, 0.15)",
                color: "#f57d36",
                fontWeight: "600",
                boxShadow: "0 2px 4px rgba(245, 125, 54, 0.1)",
              }}
              _hover={{
                bg: "rgba(245, 125, 54, 0.08)",
                color: "#f57d36",
              }}
            >
              <Flex align="center" gap={{ base: 1, md: 2 }}>
                <Icon as={MdLocalActivity} boxSize={{ base: 3.5, md: 4 }} />
                <Text fontSize={{ base: "sm", md: "md" }}>Facilities</Text>
              </Flex>
            </Tab>
            <Tab
              _focus={{ boxShadow: "none" }}
              onClick={() =>
                clickHandler("stations", getChargePointQueryParams())
              }
              borderRadius="lg"
              px={{ base: 3, md: 4 }}
              py={2}
              fontWeight="500"
              color="gray.600"
              position="relative"
              zIndex="1"
              minW="fit-content"
              whiteSpace="nowrap"
              _selected={{
                bg: "rgba(245, 125, 54, 0.15)",
                color: "#f57d36",
                fontWeight: "600",
                boxShadow: "0 2px 4px rgba(245, 125, 54, 0.1)",
              }}
              _hover={{
                bg: "rgba(245, 125, 54, 0.08)",
                color: "#f57d36",
              }}
            >
              <Flex align="center" gap={{ base: 1, md: 2 }}>
                <Icon as={MdEvStation} boxSize={{ base: 3.5, md: 4 }} />
                <Text fontSize={{ base: "sm", md: "md" }}>Chargers</Text>
              </Flex>
            </Tab>
            <Tab
              _focus={{ boxShadow: "none" }}
              onClick={() => clickHandler("ocpplogs")}
              borderRadius="lg"
              px={{ base: 3, md: 4 }}
              py={2}
              fontWeight="500"
              color="gray.600"
              position="relative"
              zIndex="1"
              minW="fit-content"
              whiteSpace="nowrap"
              _selected={{
                bg: "rgba(245, 125, 54, 0.15)",
                color: "#f57d36",
                fontWeight: "600",
                boxShadow: "0 2px 4px rgba(245, 125, 54, 0.1)",
              }}
              _hover={{
                bg: "rgba(245, 125, 54, 0.08)",
                color: "#f57d36",
              }}
            >
              <Flex align="center" gap={{ base: 1, md: 2 }}>
                <Icon as={MdArticle} boxSize={{ base: 3.5, md: 4 }} />
                <Text fontSize={{ base: "sm", md: "md" }}>OCPP Logs</Text>
              </Flex>
            </Tab>
            <RoleBasedRender roles={onlyAdmins}>
              <Tab
                _focus={{ boxShadow: "none" }}
                onClick={
                  accessLevels[loggedInUser?.role]?.accessLevel <= 2
                    ? () => clickHandler("billingplan")
                    : null
                }
                borderRadius="lg"
                px={{ base: 3, md: 4 }}
                py={2}
                fontWeight="500"
                color="gray.600"
                position="relative"
                zIndex="1"
                minW="fit-content"
                whiteSpace="nowrap"
                _selected={{
                  bg: "rgba(245, 125, 54, 0.15)",
                  color: "#f57d36",
                  fontWeight: "600",
                  boxShadow: "0 2px 4px rgba(245, 125, 54, 0.1)",
                }}
                _hover={{
                  bg: "rgba(245, 125, 54, 0.08)",
                  color: "#f57d36",
                }}
              >
                <Flex align="center" gap={{ base: 1, md: 2 }}>
                  <Icon as={MdPayment} boxSize={{ base: 3.5, md: 4 }} />
                  <Text fontSize={{ base: "sm", md: "md" }}>Billing Plan</Text>
                </Flex>
              </Tab>
            </RoleBasedRender>

            <Tab
              _focus={{ boxShadow: "none" }}
              onClick={() => clickHandler("sessions")}
              borderRadius="lg"
              px={{ base: 3, md: 4 }}
              py={2}
              fontWeight="500"
              color="gray.600"
              position="relative"
              zIndex="1"
              minW="fit-content"
              whiteSpace="nowrap"
              _selected={{
                bg: "rgba(245, 125, 54, 0.15)",
                color: "#f57d36",
                fontWeight: "600",
                boxShadow: "0 2px 4px rgba(245, 125, 54, 0.1)",
              }}
              _hover={{
                bg: "rgba(245, 125, 54, 0.08)",
                color: "#f57d36",
              }}
            >
              <Flex align="center" gap={{ base: 1, md: 2 }}>
                <Icon as={MdBolt} boxSize={{ base: 3.5, md: 4 }} />
                <Text fontSize={{ base: "sm", md: "md" }}>Charging Sessions</Text>
              </Flex>
            </Tab>

            <RoleBasedRender roles={onlyUAdmins}>
              <Tab
                _focus={{ boxShadow: "none" }}
                onClick={
                  accessLevels[loggedInUser?.role]?.accessLevel <= 2
                    ? () => clickHandler("roaming")
                    : null
                }
                borderRadius="lg"
                px={{ base: 3, md: 4 }}
                py={2}
                fontWeight="500"
                color="gray.600"
                position="relative"
                zIndex="1"
                minW="fit-content"
                whiteSpace="nowrap"
                _selected={{
                  bg: "rgba(245, 125, 54, 0.15)",
                  color: "#f57d36",
                  fontWeight: "600",
                  boxShadow: "0 2px 4px rgba(245, 125, 54, 0.1)",
                }}
                _hover={{
                  bg: "rgba(245, 125, 54, 0.08)",
                  color: "#f57d36",
                }}
              >
                <Flex align="center" gap={{ base: 1, md: 2 }}>
                  <Icon as={FaTowerBroadcast} boxSize={{ base: 3.5, md: 4 }} />
                  <Text fontSize={{ base: "sm", md: "md" }}>Roaming</Text>
                </Flex>
              </Tab>
            </RoleBasedRender>
          </>
        )}
      </TabList>

      <TabPanels>
        <TabPanel>
          <StationOverView
            getStation={props?.getStation}
            data={props?.data ? props.data : null}
            id={props?.id ? props.id : null}
            buttonTitle={props?.id ? "Save" : "Create"}
          />
        </TabPanel>
        {/* <TabPanel>
          <Photos stationId={props?.id} />
        </TabPanel> */}
        <TabPanel>
          <Location
            getStation={props?.getStation}
            id={props?.id}
            data={props?.data}
          />
        </TabPanel>
        <TabPanel>
          <Facilities
            getStation={props?.getStation}
            data={props?.data ? props.data : null}
            id={props?.id}
          />
        </TabPanel>
        <TabPanel>
          <ChargePointTabs id={props?.id} />
        </TabPanel>
        <TabPanel>
          <OcppLogs id={props?.id} />
        </TabPanel>
        <RoleBasedRender roles={onlyAdmins}>
          <TabPanel>
            <BillingPlan id={props?.id ? props.id : null} />
          </TabPanel>
        </RoleBasedRender>
        <TabPanel>
          <ChargingSessions id={props?.id ? props.id : null} />
        </TabPanel>
        <RoleBasedRender roles={onlyUAdmins}>
          <TabPanel>
            <Roaming id={props?.id ? props.id : null} />
          </TabPanel>
        </RoleBasedRender>
      </TabPanels>
    </Tabs>
  );
};

export default LocationTabs;
